import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ErrorCodes, ProblemError, useApi } from "@csa/web-foundation";
import type { Page } from "@csa/web-foundation";
import type {
  CarrierQualityHistory,
  CarrierQualitySnapshot,
  CarrierQualityView,
  CarrierView,
  CurateCarrierQualityInput,
} from "../../api/catalog-contracts";

const CARRIERS = ["catalog", "carriers"];
const qualityKey = (code: string) => [...CARRIERS, code, "quality"];

/** GET /v1/catalog/carriers — current versions, Page envelope (catalog:read @ all).
 *  `enabled` lets an RBAC-gated caller (e.g. the dashboard roster) skip a doomed request
 *  when the principal lacks catalog:read — the API still enforces regardless.
 *
 *  GAP-AUDIT-3 D13 — this used to issue ONE request with a hard `limit=50` and
 *  discard `nextCursor`, so carrier #51 onward was invisible in the catalog list AND
 *  in the five pickers built on this hook (scorecard, commission filter, commission
 *  seed, product-create, coverage-create) — a curated carrier the operator could
 *  never select. The carrier panel is a small, bounded set (dozens, curated by
 *  hand), so the honest fix is to follow the cursor to the end and hand every
 *  consumer the COMPLETE list; the return shape is unchanged, so no caller moves.
 *  MAX_PAGES is a runaway guard, not a product limit — if it is ever reached the
 *  set has outgrown "fetch it all" and this becomes a real paginated picker. */
const CARRIER_PAGE = 100;
const MAX_CARRIER_PAGES = 20;

export function useCarriers(limit = CARRIER_PAGE, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: [...CARRIERS, "list", limit],
    enabled,
    queryFn: async (): Promise<Page<CarrierView>> => {
      const all: CarrierView[] = [];
      let cursor: string | null = null;
      for (let page = 0; page < MAX_CARRIER_PAGES; page += 1) {
        const qs = new URLSearchParams({ limit: String(limit) });
        if (cursor) qs.set("cursor", cursor);
        const res: Page<CarrierView> = await api.get<Page<CarrierView>>(
          `/v1/catalog/carriers?${qs.toString()}`,
        );
        all.push(...(res.data ?? []));
        cursor = res.nextCursor ?? null;
        if (!cursor) break;
      }
      return { data: all, nextCursor: null, hasMore: false };
    },
  });
}

/** GET /v1/catalog/carriers/:code/quality — the latest snapshot, or `null` on the
 *  EXPECTED 404 cold-start (not an error). catalog:read @ all. */
export function useCarrierQuality(code: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: code ? qualityKey(code) : [...CARRIERS, "none", "quality"],
    enabled: code !== null,
    queryFn: async (): Promise<CarrierQualityView | null> => {
      try {
        return await api.get<CarrierQualityView>(`/v1/catalog/carriers/${code}/quality`);
      } catch (err) {
        if (err instanceof ProblemError && err.is(ErrorCodes.NOT_FOUND)) return null; // cold-start
        throw err;
      }
    },
  });
}

/** A1 — GET …/quality/history: the append-only series, newest first, each row carrying
 *  its own composite. `carrier_quality_snapshot` has been append-only since ADR 0004 §3,
 *  but the only read exposed was latest-by-as_of with .limit(1), so the history panel
 *  could hold exactly one row. Honest-empty: no snapshots → `data: []`, not a 404. */
export function useCarrierQualityHistory(code: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: code ? [...qualityKey(code), "history"] : [...CARRIERS, "none", "quality", "history"],
    enabled: code !== null,
    queryFn: () => api.get<CarrierQualityHistory>(`/v1/catalog/carriers/${code}/quality/history`),
  });
}

/** POST /v1/catalog/carriers/:code/quality — write a CURATED snapshot (catalog:write
 *  @ all, audited). Append-only; on success refetch the latest-by-as_of read. */
export function useCurateQuality(code: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CurateCarrierQualityInput) =>
      api.post<CarrierQualitySnapshot>(`/v1/catalog/carriers/${code}/quality`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKey(code) });
    },
  });
}
